import { Logger, MUSTNeverHappenException } from '@nu-art/ts-common';
export class IndexedDB_Store extends Logger {
    config;
    storeResolver;
    storeExistsResolver;
    // ######################## Init ########################
    constructor(config, storeResolver, storeExistsResolver) {
        super(`IDB_Store-${config.group}`);
        this.storeResolver = storeResolver;
        this.storeExistsResolver = storeExistsResolver;
        this.config = {
            ...config,
            autoIncrement: config.autoIncrement || false,
            version: config.version
        };
    }
    // ######################## DB Interaction ########################
    getStore = async (write = false, store) => this.storeResolver(this.config, write, store);
    exists = async () => this.storeExistsResolver(this.config);
    count = async () => {
        const store = await this.getStore();
        return await new Promise((resolve, reject) => {
            const countRequest = store.count();
            countRequest.onsuccess = () => {
                resolve(countRequest.result);
            };
            countRequest.onerror = () => {
                this.logError(`Failed getting count of idb store ${this.config.name}`);
                resolve(-1);
            };
        });
    };
    // ######################## Cursor Interaction ########################
    getCursor = async (query) => {
        const store = await this.getStore();
        let cursorRequest;
        if (query?.indexKey) {
            const idbIndex = store.index(query.indexKey);
            if (!idbIndex)
                throw new MUSTNeverHappenException('Could not find index for the given indexKey');
            cursorRequest = idbIndex.openCursor();
        }
        else
            cursorRequest = store.openCursor();
        return cursorRequest;
    };
    cursorHandler = (cursorRequest, perValueCallback, endCallback, limiterCallback) => {
        cursorRequest.onsuccess = (event) => {
            const cursor = event.target.result;
            //If reached the end or reached limit, call endCallback
            if (!cursor || limiterCallback?.())
                return endCallback();
            //run value through the value callback
            perValueCallback(cursor.value);
            //Continue to next value
            cursor.continue();
        };
    };
    // ######################### Data insertion functions #########################
    async insert(value, _store) {
        const store = await this.getStore(true, _store);
        return new Promise((resolve, reject) => {
            const request = store.add(value);
            request.onerror = () => reject(new Error(`Error inserting item in DB - ${this.config.name}`));
            request.onsuccess = () => resolve(request.result);
        });
    }
    async insertAll(values, _store) {
        const store = await this.getStore(true, _store);
        for (const value of values) {
            await this.insert(value, store);
        }
    }
    async upsert(value, _store) {
        const store = await this.getStore(true, _store);
        try {
            const request = store.put(value);
            return new Promise((resolve, reject) => {
                request.onerror = () => reject(new Error(`Error upserting item in DB - ${this.config.name}`));
                request.onsuccess = () => resolve(request.result);
            });
        }
        catch (e) {
            this.logError('trying to upsert: ', value);
            throw e;
        }
    }
    async upsertAll(values, _store) {
        const store = await this.getStore(true, _store);
        for (const value of values) {
            await this.upsert(value, store);
        }
    }
    // ######################### Data collection functions #########################
    async get(key) {
        const map = this.config.uniqueKeys.map(k => key[k]);
        const request = (await this.getStore()).get(map);
        return new Promise((resolve, reject) => {
            request.onerror = () => reject(new Error(`Error getting item from DB - ${this.config.name}`));
            request.onsuccess = () => resolve(request.result);
        });
    }
    async query(query) {
        const store = await this.getStore();
        return new Promise((resolve, reject) => {
            let request;
            if (query.indexKey)
                request = store.index(query.indexKey).getAll(query.query, query.limit);
            else
                request = store.getAll(query.query, query.limit);
            request.onsuccess = () => {
                resolve(request.result);
            };
            request.onerror = () => {
                reject(new Error(`Error querying DB - ${this.config.name}`));
            };
        });
    }
    async queryFilter(filter, query) {
        const limit = query?.limit || 0;
        const cursorRequest = await this.getCursor(query);
        const matches = [];
        return new Promise((resolve, reject) => {
            this.cursorHandler(cursorRequest, (value) => {
                if (filter(value))
                    matches.push(value);
            }, () => resolve(matches), () => limit > 0 && matches.length >= limit);
        });
    }
    async WIP_queryMapNew(mapper, filter, query) {
        const limit = query?.limit || 0;
        const cursorRequest = await this.getCursor(query);
        const matches = [];
        return new Promise((resolve, reject) => {
            this.cursorHandler(cursorRequest, (item) => {
                if (filter ? filter(item) : true)
                    matches.push(mapper(item));
            }, () => resolve(matches), () => limit > 0 && matches.length >= limit);
        });
    }
    async WIP_queryMap(mapper, filter, query) {
        const limit = query?.limit || 0;
        const cursorRequest = await this.getCursor(query);
        const matches = [];
        return new Promise((resolve, reject) => {
            this.cursorHandler(cursorRequest, (item) => {
                if (filter ? filter(item) : true)
                    matches.push(mapper(item));
            }, () => resolve(matches), () => limit > 0 && matches.length >= limit);
        });
    }
    async queryFind(filter) {
        let match = undefined;
        const cursorRequest = await this.getCursor();
        return new Promise((resolve, reject) => {
            this.cursorHandler(cursorRequest, (value) => {
                if (filter(value))
                    match = value;
            }, () => resolve(match), () => !!match);
        });
    }
    async queryReduce(reducer, initialValue, filter, query) {
        let acc = initialValue;
        const alwaysTrue = () => true;
        const _filter = filter || alwaysTrue;
        const matches = await this.queryFilter(_filter, query);
        return new Promise((resolve, reject) => {
            matches.forEach((item, index) => acc = reducer(acc, item, index, matches));
            resolve(acc);
        });
    }
    // ######################### Data deletion functions #########################
    async clearStore() {
        if (!(await this.exists()))
            return;
        const store = await this.getStore(true);
        return new Promise((resolve, reject) => {
            const request = store.clear();
            request.onsuccess = () => resolve();
            request.onerror = reject;
        });
    }
    async deleteAll(keys) {
        return await Promise.all(keys.map(key => this.delete(key)));
    }
    /**
     * Delete by the uniqueKey of this collection - usually _id.
     * Pass the _id of the item to delete.
     */
    async delete(key) {
        const keys = this.config.uniqueKeys.map(k => key[k]);
        const store = await this.getStore(true);
        return new Promise((resolve, reject) => {
            const itemRequest = store.get(keys);
            itemRequest.onerror = () => reject(new Error(`Error getting item in DB - ${this.config.name}`));
            itemRequest.onsuccess = () => {
                // @ts-ignore
                if (key.__updated !== undefined && itemRequest.result?.__updated > key.__updated) {
                    return resolve(itemRequest.result);
                }
                const deleteRequest = store.delete(keys);
                deleteRequest.onerror = () => reject(new Error(`Error deleting item in DB - ${this.config.name}`));
                deleteRequest.onsuccess = () => resolve(itemRequest.result);
            };
        });
    }
}
//# sourceMappingURL=IndexedDB_Store.js.map